import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  dokumentEntfernen,
  positionsDokumentHochladen,
  positionsDokumentUmhaengen,
  positionsDokumentVerknuepfen,
  positionsDokumentVersionenLaden,
  positionsDokumentVorschlaegeLaden,
  produktionsbildErsetzen,
  produktionsbildHistorieLaden,
  produktionsbildHochladen,
  produktionsbildLoeschen,
  produktionsbildWiederherstellen as produktionsbildWiederherstellenApi,
} from "../../../api/dokumente.ts";
import { produktionsbildPreviewPfad } from "../../../api/dokumente.ts";
import type {
  PositionsDokument,
  PositionsDokumenttyp,
  PositionsDokumentVersion,
  PositionsDokumentVorschlag,
  ProduktionsbildHistorienElement,
} from "../../../api/dokumente.ts";
import { ApiFehler, KonfliktFehler, ladeUndSpeichereDatei } from "../../../api/basis.ts";
import { kennungVon } from "../../../lib/bestellkennung.ts";
import { entfernenRechte } from "../../../lib/dokumentEntfernen.ts";
import { hatRecht } from "../drawerRechte.ts";
import { Ikone } from "../../Ikone.tsx";
import { Ladefehler } from "../../Ladefehler/Ladefehler.tsx";
import { geladeneDokumente, type PositionsDokumenteZustand } from "../../../lib/positionsDokumenteZustand.ts";
import { useAuthentifiziertesBild } from "../../Artikelbild.tsx";
import { UploadFehlerAnzeige } from "../../Upload/UploadFehlerAnzeige.tsx";
import { UploadAnsage, UploadFortschritt } from "../../Upload/UploadFortschritt.tsx";
import {
  uploadStelle,
  useUploadStellen,
  useUploadStellenMitPraefix,
  type UploadLauf,
  type UploadStellen,
} from "../../App/useUploadStellen.ts";
import type { Order, OrderItem } from "../../../lib/types.ts";
import { positionsbezeichnung, positionskennung } from "../../../lib/positionsAnzeige.ts";
import { meldeFehler, meldeNachladeFehler } from "../../../state/fehlerStapel.ts";
import { useMontiertRef } from "../../../state/useMontiertRef.ts";
import { ArtworkPruefungAbzeichen, fokusZumAbzeichen, zeileDesAbzeichens } from "./ArtworkPruefung/ArtworkPruefungAbzeichen.tsx";
import { ArtworkPruefungEinzelheitenLazy } from "./ArtworkPruefung/ArtworkPruefungEinzelheitenLazy.tsx";
import { fassungsSchluessel, nichtGeprueft, pruefungDerFassung } from "./ArtworkPruefung/fassung.ts";
import { anzeigeVeraltet, pruefAktionen } from "./ArtworkPruefung/pruefAktionen.ts";
import type { Melder } from "./ArtworkPruefung/melder.ts";
import { useArtworkPruefungen } from "./ArtworkPruefung/useArtworkPruefungen.ts";
import {
  POSITION_BELEGT_TEXT,
  NachladenNachUploadFehler,
  dateiZuGrossText,
  istZuGross,
  stehenderHinweisText,
  uploadFehlerText,
} from "../../../lib/upload.ts";

/**
 * Der Satz `anzeigeVeraltet` fuer ein gelungenes Schreiben, dessen Nachladen scheiterte (Spec R4, R-Z2), steht in
 * `ArtworkPruefung/pruefAktionen.ts`: die Zusammenfassung des Massenuploads braucht dieselbe Fassung.
 *
 * Dasselbe fuer die Liste „Entfernte Dokumente“ (Ticket F): das Entfernen bzw. Loeschen ist gelungen, nur die Liste, in der
 * die Datei jetzt steht, ist veraltet. Wie bei `anzeigeVeraltet` haengt `meldeNachladeFehler` die Ursache an, ausser sie raet zum
 * Wiederholen - ein „erneut versuchen“ hinter „ist entfernt“ liesse den Nutzer das naechste Dokument entfernen.
 */
const entfernteListeVeraltet = (zustand: string): string =>
  `${zustand}, die Liste „Entfernte Dokumente“ konnte aber nicht aktualisiert werden. Bitte neu laden.`;

/**
 * Der Fehlerstapel für die Artwork-Prüfung: ihre Module (`ArtworkPruefung/*`) melden über diesen übergebenen Melder, nicht über
 * einen eigenen Import von `state/fehlerStapel.ts` - Grund in `ArtworkPruefung/melder.ts`.
 *
 * Dieselbe Zeile steht auch in `Massenupload/useArtworkPruefungAbschnitt.tsx`. Die Doppelung ist erzwungen: ein gemeinsames Modul
 * dafür importierte den Fehlerstapel von beiden Seiten und öffnete die Rolldown-Falle wieder (Kopf von `ArtworkPruefung/melder.ts`).
 */
const PRUEF_MELDER: Melder = { meldeFehler, meldeNachladeFehler };

/**
 * Artwork entfernen (Ticket F): Wortlaut fuer ein gescheitertes `DELETE .../document-links/:linkId`. Ein 409 (die Zeile traegt eine
 * veraltete `rowVersion`) bekommt den Sondertext, wie `DokumenteTab.tsx#DOKUMENT_ENTFERNEN_KONFLIKT_MELDUNG` ihn fuer Bestelldokumente
 * hat; alles andere geht als `kontext` vor die Ursache aus `fehlertext()`.
 */
export const ARTWORK_ENTFERNEN_FEHLGESCHLAGEN_MELDUNG = "Das Artwork konnte nicht entfernt werden.";
export const ARTWORK_ENTFERNEN_KONFLIKT_MELDUNG = "Das Artwork wurde inzwischen geändert, bitte neu laden.";

/**
 * Dokument ohne Position umhaengen (Positionsersatz C1): Wortlaut fuer ein gescheitertes `POST .../document-links/:linkId/umhaengen`.
 * Nur ein 409 MIT aktuellem Stand (die Zeile hat sich geaendert) bekommt den Sondertext. Ein 409 ohne (das Ziel hat schon ein
 * Artwork - nur im Wettlauf, denn die Auswahl sperrt solche Ziele), 422 (das Ziel ist keine Position der Bestellung mehr) und alles
 * andere gehen als `kontext` vor die Ursache aus `fehlertext()`, damit der Servertext ("Position 22500 hat schon ein Artwork.")
 * sichtbar wird.
 */
export const UMHAENGEN_FEHLGESCHLAGEN_MELDUNG = "Das Dokument konnte nicht umgehängt werden.";
export const UMHAENGEN_KONFLIKT_MELDUNG = "Das Dokument wurde inzwischen geändert, bitte neu laden.";

/**
 * Ereignisse der Galerie-Historie, deren Rohwert (`neuWert`, das Ereignis selbst: `erstellt`, `ersetzt`, `geloescht` ...) nicht lesbar ist.
 * Spiegel von `PRODUKTIONSBILD_EREIGNIS_TEXT` im Backend (`documents/repository.ts`) - bisher nur `umgehaengt`; die uebrigen
 * Ereignisse zeigt die Historie weiter roh, wie bisher.
 */
const GALERIE_EREIGNIS_TEXT: ReadonlyMap<string, string> = new Map([["umgehaengt", "umgehängt"]]);

/** Ohne Angabe der Rechte (Aufrufer ohne Drawer) gibt es keinen Muelleimer: im Zweifel nicht anbieten. */
const KEINE_RECHTE: ReadonlySet<string> = new Set();

/** Der Satz der Flaeche, wenn das Laden der Positionsdokumente scheiterte (L1). */
const POSITIONSDOKUMENTE_LADEFEHLER_SATZ = "Die Positionsdokumente konnten nicht geladen werden.";

const DOKUMENTTYPEN: readonly { typ: PositionsDokumenttyp; label: string }[] = [
  { typ: "artwork", label: "Artwork" },
  { typ: "produktionsbilder", label: "Produktionsbilder" },
];

export type ProduktionsbildOeffnen = (linkId: string, ausloeser: HTMLElement) => void;

interface PositionsdokumenteProps {
  bestellung: Order;
  /**
   * Die Positionsdokumente in drei Ladezustaenden (Fehleranzeige Paket 2, Task 13, Spec E4, Ruling R-T13a). Nur mit `geladen` zeigt der
   * Abschnitt Positionen, Knoepfe, den Block „Dokumente ohne Position“ und „Vorhandenes Positionsdokument uebernehmen“ - alle rechnen
   * aus der Liste, und auf unbekanntem Stand ist unklar, ob eine Position schon ein Artwork hat (Sperre im Umhaengen-Ziel,
   * Fassungskette). `gescheitert` zeigt statt dessen EINE Flaeche mit Grund und „Erneut laden“, `laedt` einen neutralen Hinweis.
   */
  positionsDokumenteZustand: PositionsDokumenteZustand;
  positionsDokumenteNeuLaden: () => Promise<void>;
  /**
   * „Erneut laden“ der Flaeche (nur im Zustand `gescheitert`). Der Rueckruf lehnt NIE ab und ist erst nach dem Laden erfuellt
   * (`<Ladefehler>`: die Flaeche bleibt waehrend des Ladens im Baum; `App.tsx#positionsDokumenteErneutLaden`). Ohne ihn gibt es keinen Knopf.
   */
  onPositionsDokumenteErneutLaden?: () => Promise<void>;
  vorausgewaehltePosition?: number | null;
  onProduktionsbildOeffnen?: ProduktionsbildOeffnen;
  /**
   * `App.tsx#zeigeHinweis` (ueber `DokumenteTab`): der Fehlschlag eines Uploads, fuer den dieser Abschnitt keine
   * Flaeche mehr hat - er ist abgebaut oder zeigt inzwischen eine andere Bestellung. Wie im Positionen-Grid
   * (`PositionsdokumentAktionen.tsx#onStehenderHinweis`): der Text nennt Bezeichnung, Bestellnummer und
   * Artikelnummer vom START des Uploads (Fix-Runde 1 zu Task 5, Punkt 6).
   */
  onStehenderHinweis?: (text: string) => void;
  /**
   * Die Rechte des Nutzers (`DokumenteTab`): der Muelleimer am Artwork erscheint nur mit dem Recht, das auch das Hochladen
   * verlangt (`lib/dokumentEntfernen.ts#entfernenRechte`, `manage_documents`) - die uebrigen Knoepfe dieses Abschnitts sperrt
   * allein der Rechtedurchgang des Drawers, hier verlangt die Oberflaeche „sichtbar nur mit Recht“ (Ticket F). Der Server
   * urteilt ohnehin verbindlich (403). Fehlt die Angabe, gibt es keinen Muelleimer.
   */
  rechte?: ReadonlySet<string>;
  /**
   * Laedt die Liste „Entfernte Dokumente“ des Reiters neu (`DokumenteTab`): nach dem Entfernen eines Artworks und dem
   * Loeschen eines Produktionsbilds steht die Datei dort. Lehnt ab, wenn das Laden scheitert - der Satz dazu kommt von hier
   * (`entfernteListeVeraltet`). Ohne die Angabe bleibt die Liste auf ihrem Stand.
   */
  entfernteNeuLaden?: () => Promise<void>;
  /**
   * Der Store der Upload-Stellen (`App.tsx` ueber `DokumenteTab`, Spec 2026-10-20): die Sperre je Position und die
   * Fortschrittszeilen leben dort und ueberleben Reiter- und Bestellungswechsel. Ohne Prop legt der Abschnitt sich
   * einen eigenen an - die Sperre gilt dann nur fuer diese Instanz (das Verhalten vor den Upload-Sperren).
   */
  uploadStellen?: UploadStellen;
  /**
   * Zählt, wie oft der Massenupload-Dialog dieser Bestellung geschlossen wurde (`App.tsx`, `massenuploadSchliessen`): „Gesehen“ und
   * „Erneut prüfen“ in dessen Zusammenfassung ändern den Prüfstand, den dieser Reiter als eigene Hook-Instanz führt. Ein neuer
   * Wert startet den Lauf des Prüfstands neu (Teil des Ladeschlüssels, `fassungsSchluessel`). Ohne Angabe: 0.
   */
  massenuploadGeschlossen?: number;
  /**
   * `App.tsx#dokumentBestellungNachladen` (ueber `DokumenteTab`): laedt die Bestellung in den Store nach, damit die Board-Marke
   * „Artwork prüfen“ nach „Gesehen“ und „Erneut prüfen“ ihre Zahl wechselt (`pruefAktionen`). Ohne die Angabe bleibt das Board
   * bis zum naechsten Laden auf seinem Stand.
   */
  bestellungNachladen?: () => Promise<void>;
}

type Historie = {
  typ: "artwork";
  versionen: PositionsDokumentVersion[];
} | {
  typ: "produktionsbilder";
  bilder: ProduktionsbildHistorienElement[];
};

type AusstehenderErsatz = {
  typ: "artwork";
  datei: File;
  jtlPosId: number;
  rowVersion: number;
} | {
  typ: "produktionsbilder";
  datei: File;
  jtlPosId: number;
  rowVersion: number;
  linkId: string;
};

interface HistorieLadeKontext {
  bestellungId: string;
  jtlPosId: number;
  typ: Historie["typ"];
  ladefolge: number;
}

/**
 * Bestell- und Artikelnummer werden BEIM START des Uploads festgehalten, nicht beim Scheitern gelesen: bis ein
 * Upload von 100 MB scheitert, zeigt dieselbe Instanz womoeglich eine andere Bestellung, und die Props waeren die
 * der falschen (dasselbe Muster wie `UploadKontext` in `PositionsdokumentAktionen.tsx`).
 */
interface UploadKontext {
  bestellungId: string;
  bestellNummer: string;
  artikelNummer: string | null;
  typ: PositionsDokumenttyp;
}

function ProduktionsbildThumbnail({
  bestellungId,
  bild,
  onProduktionsbildOeffnen,
}: {
  bestellungId: string;
  bild: PositionsDokument;
  onProduktionsbildOeffnen?: ProduktionsbildOeffnen;
}) {
  const pfad = produktionsbildPreviewPfad(bestellungId, bild.jtlPosId, bild.linkId, "thumbnail", bild.fileId);
  const { objectUrl, fehlgeschlagen, meldeAnzeigefehler } = useAuthentifiziertesBild(pfad, true, undefined);

  if (fehlgeschlagen) return <p className="produktionsbild-vorschau-fehler">Vorschau nicht verfügbar</p>;
  if (!objectUrl) return null;

  return <button
    type="button"
    className="produktionsbild-thumb"
    onClick={(event) => onProduktionsbildOeffnen?.(bild.linkId, event.currentTarget)}
  >
    <img src={objectUrl} alt={`${bild.originalName} Vorschau`} onError={meldeAnzeigefehler} />
  </button>;
}

/**
 * Eigene Pflegeflaeche fuer die zwei positionsgebundenen Dokumenttypen. Bestellweite Dokumente
 * bleiben bewusst ausschliesslich im umgebenden Dokumente-Tab.
 */
export function Positionsdokumente({
  bestellung,
  positionsDokumenteZustand,
  positionsDokumenteNeuLaden,
  onPositionsDokumenteErneutLaden,
  vorausgewaehltePosition,
  onProduktionsbildOeffnen,
  onStehenderHinweis,
  rechte = KEINE_RECHTE,
  entfernteNeuLaden,
  uploadStellen,
  massenuploadGeschlossen = 0,
  bestellungNachladen,
}: PositionsdokumenteProps) {
  const bestellungId = bestellung.id as string;
  // Aus dem Zustand wird nur im geladenen Stand eine Liste; sonst rechnen die Ableitungen unten mit einer leeren, und gezeichnet wird
  // nichts davon (siehe die Bedingungen im Rueckgabewert).
  const geladen = positionsDokumenteZustand.art === "geladen";
  const positionsDokumente = geladeneDokumente(positionsDokumenteZustand);
  const darfArtworkEntfernen = hatRecht(rechte, entfernenRechte("artwork"));
  /** „Prüfen“, „Erneut prüfen“ und „Gesehen“ verlangen wie Hochladen und Entfernen `manage_documents`; der Server urteilt verbindlich. */
  const darfPruefungAendern = hatRecht(rechte, "manage_documents");
  const [historie, setHistorie] = useState<Historie | null>(null);
  const [quellDokumentId, setQuellDokumentId] = useState("");
  /** Positionsdokumente ANDERER Bestellungen mit gleichem Artikel (Positionsersatz C2); das Backend blendet aus, was die eigene Auswahl schon zeigt. */
  const [vorschlaege, setVorschlaege] = useState<readonly PositionsDokumentVorschlag[]>([]);
  /** Der laufende Abruf der Vorschlaege: ein neuer, ein Bestellungswechsel und der Abbau brechen den alten ab. */
  const vorschlaegeAbbruch = useRef<AbortController | null>(null);
  const [ausstehenderErsatz, setAusstehenderErsatz] = useState<AusstehenderErsatz | null>(null);
  const [ersatznotiz, setErsatznotiz] = useState("");
  const [ausgeblendeteProduktionsbildLinks, setAusgeblendeteProduktionsbildLinks] = useState<readonly string[]>([]);
  /**
   * Der Fehlschlag eines Uploads (Task 6 "Uploads bis 100 MB"): zu gross, Verbindung, Zeitlimit ... als stehende Zeile
   * (`UploadFehlerAnzeige`). Nur Uploads setzen ihn - jeder andere Aktionsfehler dieses Abschnitts (Download, Historie,
   * Uebernehmen, Loeschen, Wiederherstellen, Nachladen) geht in den Fehlerstapel (Fehleranzeige Paket 1). Ein
   * gescheiterter Upload steht NICHT zusaetzlich im Stapel (keine Doppelmeldung).
   */
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * Doppelklick-Schutz fuer Artwork entfernen und Produktionsbild loeschen (wie `DokumenteTab.tsx#entfernenLaeuft`): solange
   * ein DELETE samt Nachladen laeuft, traegt die Zeile noch die alte `rowVersion` - ein zweiter Klick endete im 409. Der Ref ist
   * die synchrone Sperre (zwei Klicks im selben Zeichentakt), der State sperrt die Knoepfe.
   */
  const entfernenLaeuft = useRef(false);
  const [entfernenAktiv, setEntfernenAktiv] = useState(false);
  /** Die gewaehlte Zielposition je Dokument ohne Position (Schluessel `linkId`, Wert die JTL-Positions-Id als Text; leer = keine). */
  const [umhaengenZiele, setUmhaengenZiele] = useState<Readonly<Record<string, string>>>({});
  /**
   * Die Laeufe der Positionen dieser Bestellung (Spec 2026-10-20): jede gesperrte Position mit ihrer
   * Fortschrittszeile. Der Praefix folgt der gezeigten Bestellung; ein Reiterwechsel baut diesen Abschnitt ab, ein
   * Bestellungswechsel behaelt die Instanz (in jedem Ladezustand, auch ohne Recht oder UUID: `DokumenteTab` haengt den Abschnitt immer an dieselbe Stelle,
   * die Ablage des Massenuploads bleibt eingehaengt) - die Laeufe liegen im Store der App und ueberstehen beides.
   */
  const lokaleStellen = useUploadStellen();
  const stellen = uploadStellen ?? lokaleStellen;
  const laeufe = useUploadStellenMitPraefix(stellen, uploadStelle.positionenVon(bestellungId));
  const dateiInputs = useRef(new Map<string, HTMLInputElement>());
  const historieLadefolge = useRef(0);
  const bestellungIdRef = useRef(bestellungId);
  const historieKontextRef = useRef<HistorieLadeKontext | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const historieDialogRef = useRef<HTMLElement | null>(null);
  const ersatznotizRef = useRef<HTMLTextAreaElement | null>(null);
  const historieSchliessenRef = useRef<HTMLButtonElement | null>(null);
  const historieAusloeserRef = useRef<HTMLButtonElement | null>(null);
  const ersatzAusloeserRef = useRef<HTMLButtonElement | null>(null);
  const positionsRefs = useRef(new Map<string, HTMLElement>());
  bestellungIdRef.current = bestellungId;
  // Ob dieser Abschnitt noch im Baum steht (der Drawer-Reiter wechselt: `.drawer-body` hat `key={tab:rechte}`).
  // `"layout"`, weil der passive Cleanup der Router-Transition erst spaeter kaeme: ein Upload, der dazwischen scheitert, setzte
  // die Zeile an die schon abgebaute Instanz (verloren), statt als stehender Hinweis hinauszugehen (`uploadFehlerMelden`).
  const montiertRef = useMontiertRef("layout");
  const positionen = (bestellung.items || []) as OrderItem[];
  const hatPersistiertePositionen = positionen.some((position) => positionskennung(position) !== null);
  const aktuellesDokument = (jtlPosId: number, typ: "artwork"): PositionsDokument | undefined =>
    positionsDokumente.find((dokument) => dokument.jtlPosId === jtlPosId && dokument.docType === typ);

  /**
   * Artwork-Prüfung (Artwork-Abgleich, Task 10): der Prüfstand der Bestellung, je Artwork mit Abzeichen und Einzelheiten.
   * Der Schlüssel besteht aus Link, Version und Position jedes aktuellen Artworks: eine neue Version reiht eine neue Prüfung
   * ein, ebenso das Umhängen an eine andere Position (gleicher Link, gleiche Version, anderer Artikel). Bei Änderung lädt
   * der Hook neu; das Ergebnis einer laufenden Prüfung holt sein Polling ohne Schlüsselwechsel.
   */
  const artworks = positionsDokumente.filter((dokument) => dokument.docType === "artwork");
  const { nachLink: pruefungen, neuLaden: pruefungenNeuLaden, urteil } = useArtworkPruefungen(
    bestellungId || null,
    fassungsSchluessel(artworks, massenuploadGeschlossen),
    artworks,
    PRUEF_MELDER,
  );
  const pruefungVon = (dokument: PositionsDokument) => pruefungDerFassung(pruefungen.get(dokument.linkId), dokument, urteil);
  /** Die `linkId` des Artworks, dessen Einzelheiten offen stehen. */
  const [pruefungOffen, setPruefungOffen] = useState<string | null>(null);
  const pruefungAusloeserRef = useRef<HTMLElement | null>(null);
  /** Die Zeile des Auslösers: bleibt stehen, wenn aus dem Knopf eine Beschriftung wird („Stand veraltet“) - dorthin geht dann der Fokus. */
  const pruefungZeileRef = useRef<HTMLElement | null>(null);
  // Ist der Stand eines Artworks „veraltet“, gibt es keine Einzelheiten dazu (das Abzeichen ist kein Knopf): sie schließen.
  const offenesArtwork = pruefungOffen === null
    ? undefined
    : positionsDokumente.find((dokument) => (
      dokument.docType === "artwork" && dokument.linkId === pruefungOffen && !pruefungVon(dokument)?.veraltet
    ));
  const produktionsbilder = (jtlPosId: number): readonly PositionsDokument[] => positionsDokumente.filter((dokument) => (
    dokument.jtlPosId === jtlPosId
    && dokument.docType === "produktionsbilder"
    && !ausgeblendeteProduktionsbildLinks.includes(dokument.linkId)
  ));

  /**
   * Dokumente ohne Position (Positionsersatz C1): ihre `jtlPosId` kommt unter den Positionen der Bestellung nicht mehr vor - JTL hat
   * die Position ersetzt oder gestrichen. Das Grid zeichnet nur Positionen und sah sie deshalb nie. Ein lokal schon ausgeblendetes
   * Produktionsbild (Loeschen laeuft, die Liste ist noch nicht neu geladen) zaehlt nicht mehr mit.
   */
  const aktuelleKennungen = new Set(positionen.map(positionskennung).filter((kennung): kennung is number => kennung !== null));
  const waisen = positionsDokumente.filter((dokument) => (
    !aktuelleKennungen.has(dokument.jtlPosId) && !ausgeblendeteProduktionsbildLinks.includes(dokument.linkId)
  ));
  /** Die Zielpositionen: alle Positionen mit JTL-Kennung, mit der Bezeichnung des Grids (laufende Nummer ueber ALLE Positionen). */
  const zielPositionen = positionen.flatMap((position, index) => {
    const jtlPosId = positionskennung(position);
    return jtlPosId === null ? [] : [{ jtlPosId, bezeichnung: positionsbezeichnung(position, index) }];
  });
  /** Ein Artwork darf nicht auf eine Position mit Artwork (eine Fassungskette je Position); Produktionsbilder haengen ueberall. */
  const umhaengenZielGesperrt = (dokument: PositionsDokument, jtlPosId: number): boolean => (
    dokument.docType === "artwork" && aktuellesDokument(jtlPosId, "artwork") !== undefined
  );
  /** Das gewaehlte Ziel, sofern es noch eines ist: ein inzwischen belegtes Artwork-Ziel (Neuladen) gilt als nicht gewaehlt. */
  const gewaehltesZiel = (dokument: PositionsDokument) => zielPositionen.find((ziel) => (
    String(ziel.jtlPosId) === umhaengenZiele[dokument.linkId] && !umhaengenZielGesperrt(dokument, ziel.jtlPosId)
  ));

  const inputSchluessel = (typ: string, jtlPosId: number, linkId = ""): string => `${typ}:${jtlPosId}:${linkId}`;

  /** Der Kontext eines Uploads in DIESEM Augenblick - beim Start aufrufen, nicht beim Scheitern. */
  const uploadKontext = (typ: PositionsDokumenttyp, jtlPosId: number): UploadKontext => ({
    bestellungId,
    bestellNummer: kennungVon(bestellung, bestellungId),
    artikelNummer: (positionen.find((position) => positionskennung(position) === jtlPosId)?.sku as string | undefined) || null,
    typ,
  });

  /**
   * Ein gescheiterter Upload. Steht der Abschnitt noch da UND zeigt dieselbe Bestellung wie beim Start, bleibt
   * es bei der Zeile an Ort und Stelle. Sonst gaebe es keine Stelle mehr, an der sie zum Upload passt (sie
   * stuende unter der NEUEN Bestellung, oder sie ginge mit dem Abbau verloren): dann geht der Fehler als
   * stehender Hinweis hinaus - `Artwork für BE-10440 (301020): <Fehlertext>`, die Klammer entfaellt ohne
   * Artikelnummer. Die Bezeichnungen sind die des Grids (`PositionsdokumentAktionen.tsx#DOKUMENTTYPEN`).
   */
  const uploadFehlerMelden = (kontext: UploadKontext, ursache: unknown, datei: File): void => {
    const text = uploadFehlerText(ursache, datei);
    if (montiertRef.current && bestellungIdRef.current === kontext.bestellungId) {
      setFehler(text);
      return;
    }
    const label = DOKUMENTTYPEN.find((eintrag) => eintrag.typ === kontext.typ)?.label ?? "Positionsdokument";
    onStehenderHinweis?.(stehenderHinweisText({ label, bestellNummer: kontext.bestellNummer, artikelNummer: kontext.artikelNummer }, text));
  };

  /** Die Quelle von „Vorhandenes Positionsdokument uebernehmen“: ein eigenes Dokument der Bestellung oder ein Vorschlag aus einer anderen. */
  const eigenesQuellDokument = positionsDokumente.find((dokument) => dokument.documentId === quellDokumentId);
  const gewaehlterVorschlag = eigenesQuellDokument ? undefined : vorschlaege.find((vorschlag) => vorschlag.documentId === quellDokumentId);
  const quellDokument = eigenesQuellDokument ?? gewaehlterVorschlag;
  /**
   * Ein Vorschlag aus einer anderen Bestellung gilt nur fuer Positionen mit seinem Artikel (Spec C2): `position.sku`, wie bei
   * `uploadKontext`, getrimmt verglichen. Eigene Dokumente der Bestellung haengen weiter an jeder Position.
   */
  const quelleGiltFuerPosition = (jtlPosId: number): boolean => (
    gewaehlterVorschlag === undefined
    || String(positionen.find((position) => positionskennung(position) === jtlPosId)?.sku ?? "").trim() === gewaehlterVorschlag.artikelNummer.trim()
  );
  /** Der Grund, warum „Uebernehmen“ an dieser Position gesperrt ist, als `title`; ohne Sperre `undefined`. */
  const nurAnPositionenMitArtikel = (jtlPosId: number): string | undefined => (
    quelleGiltFuerPosition(jtlPosId) || gewaehlterVorschlag === undefined
      ? undefined
      : `Nur an Positionen mit Artikel ${gewaehlterVorschlag.artikelNummer.trim()}`
  );

  /**
   * Laedt die Vorschlaege dieser Bestellung. Der Abruf ist abbrechbar: jeder neue Abruf bricht den laufenden ab, der Effekt auf
   * `bestellungId` auch beim Wechsel und beim Abbau. Ein Abbruch bleibt still; jeder andere Fehler geht in den Fehlerstapel, die
   * eigenen Dokumente bleiben waehlbar. Ein Aufruf aus einem Render mit einer inzwischen ersetzten Bestellung tut nichts.
   *
   * Ausnahme 404: eine JTL-Bestellung ohne Hub-Zeile hat die Kennung `jtl-<n>`, und die Route antwortet auf jede Nicht-UUID mit 404
   * („Bestellung nicht gefunden.“). Das ist der Normalfall und heisst „keine Vorschlaege“ - wie bei den Nachbar-Ladern beim Oeffnen
   * (`App.tsx`, Positionsdokumente, Dokumente, Checkliste) bleibt es still. Dort gaebe es auch nichts zu uebernehmen: ohne Hub-Zeile
   * kann die Bestellung keine Positionsdokumente tragen (`positionskontext` im Backend).
   */
  const vorschlaegeLaden = async (): Promise<void> => {
    // Endet eine Aktion erst, nachdem der Abschnitt verlassen wurde oder die Bestellung wechselte, ruft sie nichts mehr ab.
    if (!montiertRef.current || bestellungIdRef.current !== bestellungId) return;
    vorschlaegeAbbruch.current?.abort();
    const abbruch = new AbortController();
    vorschlaegeAbbruch.current = abbruch;
    try {
      const liste = await positionsDokumentVorschlaegeLaden(bestellungId, abbruch.signal);
      if (abbruch.signal.aborted || !montiertRef.current) return;
      setVorschlaege(liste);
    } catch (ursache) {
      if (abbruch.signal.aborted) return;
      if (ursache instanceof ApiFehler && ursache.status === 404) {
        if (montiertRef.current && bestellungIdRef.current === bestellungId) setVorschlaege([]);
        return;
      }
      console.error("Vorschläge aus anderen Bestellungen konnten nicht geladen werden:", ursache);
      meldeFehler(ursache, "Vorschläge aus anderen Bestellungen konnten nicht geladen werden.");
    }
  };

  useEffect(() => {
    setVorschlaege([]);
    void vorschlaegeLaden();
    return () => {
      vorschlaegeAbbruch.current?.abort();
    };
  }, [bestellungId]);

  /**
   * Laedt die eigenen Positionsdokumente neu (Rueckruf des Aufrufers) UND die Vorschlaege: das Backend blendet aus ihnen aus, was die
   * eigene Auswahl zeigt - ein Uebernehmen, Entfernen, Umhaengen oder Hochladen aendert also auch sie, und eine wieder freie Datei
   * soll nicht erst beim naechsten Oeffnen auftauchen. Die Vorschlaege werden nicht abgewartet: `vorschlaegeLaden` meldet seine
   * Fehler selbst und lehnt nie ab, und es soll weder das Nachladen noch die Upload-Sperre (`mitFortschritt`) verzoegern. Keine
   * Schleife: `vorschlaegeLaden` ruft den Rueckruf nie auf.
   */
  const positionsDokumenteUndVorschlaegeNeuLaden = (): Promise<void> => {
    void vorschlaegeLaden();
    return positionsDokumenteNeuLaden();
  };

  useEffect(() => {
    bestellungIdRef.current = bestellungId;
    historieLadefolge.current += 1;
    historieKontextRef.current = null;
    setHistorie(null);
    setQuellDokumentId("");
    setAusstehenderErsatz(null);
    setErsatznotiz("");
    setAusgeblendeteProduktionsbildLinks([]);
    setUmhaengenZiele({});
    setFehler(null);
    setPruefungOffen(null);
  }, [bestellungId]);

  // Das Artwork, dessen Prüfung offen steht, ist weg (entfernt, umgehängt in eine andere Bestellung) oder sein Stand ist „veraltet“
  // (aus dem Auslöser ist eine Beschriftung geworden): die Einzelheiten schließen, der Fokus geht an das Abzeichen derselben Zeile.
  useEffect(() => {
    if (pruefungOffen === null || offenesArtwork !== undefined) return;
    setPruefungOffen(null);
    fokusZumAbzeichen(pruefungAusloeserRef.current, pruefungZeileRef.current);
  }, [offenesArtwork, pruefungOffen]);

  useEffect(() => {
    if (quellDokumentId && !quellDokument) setQuellDokumentId("");
  }, [quellDokument, quellDokumentId]);

  useEffect(() => {
    setAusgeblendeteProduktionsbildLinks((links) => links.filter((linkId) => (
      positionsDokumente.some((dokument) => dokument.linkId === linkId)
    )));
  }, [positionsDokumente]);

  const modalAktiv = historie !== null || ausstehenderErsatz !== null;

  useLayoutEffect(() => {
    if (!modalAktiv) return;
    if (ausstehenderErsatz) ersatznotizRef.current?.focus();
    else historieSchliessenRef.current?.focus();
  }, [ausstehenderErsatz, historie, modalAktiv]);

  useLayoutEffect(() => {
    if (modalAktiv || !Number.isInteger(vorausgewaehltePosition)) return;
    positionsRefs.current.get(`artwork:${vorausgewaehltePosition}`)?.focus();
  }, [modalAktiv, positionen, vorausgewaehltePosition]);

  useEffect(() => {
    if (!modalAktiv) return;
    const aktiverDialog = (): HTMLElement | null => historie ? historieDialogRef.current : dialogRef.current;
    const fokusierbareElemente = (): HTMLElement[] => Array.from(
      aktiverDialog()?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || [],
    );
    const fokusSichern = (event: FocusEvent): void => {
      if (aktiverDialog()?.contains(event.target as Node)) return;
      fokusierbareElemente()[0]?.focus();
    };
    const beiTab = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && historie) {
        event.preventDefault();
        historieSchliessen();
        return;
      }
      if (event.key !== "Tab") return;
      const elemente = fokusierbareElemente();
      if (!elemente.length) return;
      const erstes = elemente[0];
      const letztes = elemente[elemente.length - 1];
      if (event.shiftKey && document.activeElement === erstes) {
        event.preventDefault();
        letztes.focus();
      } else if (!event.shiftKey && document.activeElement === letztes) {
        event.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("focusin", fokusSichern, true);
    document.addEventListener("keydown", beiTab);
    return () => {
      document.removeEventListener("focusin", fokusSichern, true);
      document.removeEventListener("keydown", beiTab);
    };
  }, [historie, modalAktiv]);

  const istAktuelleHistorie = (kontext: HistorieLadeKontext): boolean => {
    const aktuellerKontext = historieKontextRef.current;
    if (!aktuellerKontext) return false;
    return historieLadefolge.current === kontext.ladefolge
      && bestellungIdRef.current === kontext.bestellungId
      && aktuellerKontext.bestellungId === kontext.bestellungId
      && aktuellerKontext.jtlPosId === kontext.jtlPosId
      && aktuellerKontext.typ === kontext.typ;
  };

  const dateiOeffnen = async (fileId: string, dateiname: string): Promise<void> => {
    try {
      await ladeUndSpeichereDatei(`/api/files/${encodeURIComponent(fileId)}`, dateiname);
    } catch (ursache) {
      // Aktionsfehler dieser Fehlerzeile am Tab-Ende gehen an den Stapel (Spec R2); der feste Satz
      // wird zum Kontext, die Ursache kommt aus `fehlertext()`.
      meldeFehler(ursache, "Die Datei konnte nicht heruntergeladen werden.");
    }
  };

  const historieInvalidieren = (): void => {
    historieLadefolge.current += 1;
    historieKontextRef.current = null;
    setHistorie(null);
  };

  /**
   * Laedt die Positionsdokumente neu (und, ohne darauf zu warten, die Vorschlaege aus anderen Bestellungen, siehe
   * `positionsDokumenteUndVorschlaegeNeuLaden`). Scheitert das NACH einem gelungenen Schreiben, steht das Schreiben
   * trotzdem (Spec R4): die Meldung sagt, was gespeichert ist, und haengt die Ursache des Nachladens an.
   * Liefert `false`, wenn das Nachladen scheiterte. Gilt fuer alle Schreibvorgaenge ausser dem Upload (Uebernehmen,
   * Loeschen, Wiederherstellen): der Upload laedt selbst nach und meldet einen Fehlschlag als Zeile.
   *
   * Der Satz hier greift nur, wenn der Rueckruf ablehnt. Der der App tut das heute nicht, er meldet selbst (R-M16, derselbe
   * Satz-Gedanke: gespeichert, Anzeige veraltet, kein Wiederholungsrat); dann kommt `nachladen` mit `true` zurueck.
   */
  const nachladen = async (zustand: string): Promise<boolean> => {
    try {
      await positionsDokumenteUndVorschlaegeNeuLaden();
      return true;
    } catch (ursache) {
      console.error("Positionsdokumente konnten nicht neu geladen werden:", ursache);
      meldeNachladeFehler(ursache, anzeigeVeraltet(zustand));
      return false;
    }
  };

  /**
   * Sperre je Position (Spec Z. 51): solange ein Upload an einer Position laeuft, startet dort kein zweiter - zwei
   * Artwork-Uploads mit derselben `rowVersion` liefen sonst parallel, der zweite endete im 409. Gilt fuer alle
   * Dokumenttypen der Position; andere Positionen bleiben frei. Die Sperre liegt im Store der App (Schluessel
   * `position:<bestellungId>:<jtlPosId>`), nicht in diesem Abschnitt.
   *
   * `positionBelegt` ist der Wert fuer den RENDER (aus dem Abo, `laeufe`): die Eingaben und Knoepfe sind
   * `disabled`. `positionGesperrt` fragt den Store SYNCHRON, fuer die Handler - ein Render-Wert gilt erst nach dem
   * naechsten Zeichnen, und ein Ereignis darf die gesperrte Eingabe trotzdem erreichen.
   */
  const positionBelegt = (jtlPosId: number): boolean => {
    const stelle = uploadStelle.position(bestellungId, jtlPosId);
    return laeufe.some((eintrag) => eintrag.stelle === stelle);
  };
  const positionGesperrt = (jtlPosId: number): boolean => stellen.istGesperrt(uploadStelle.position(bestellungId, jtlPosId));

  /**
   * Vorabpruefung (Task 6): eine Datei ueber der Handupload-Grenze wird nie gesendet, sie ginge nur
   * mit einem 413 zurueck - und fuer einen Ersatz soll sie nicht erst den Begruendungsdialog
   * oeffnen. `true`, wenn sie hochgeladen werden darf; sonst steht der Grund schon im Fehlerfeld.
   */
  const dateiZulaessig = (datei: File): boolean => {
    if (!istZuGross(datei)) return true;
    setFehler(dateiZuGrossText(datei));
    return false;
  };

  /**
   * Beginnt den Lauf der Position EXKLUSIV (`versucheBeginn`, synchron) an der Position DES STARTS: `bestellungId` ist
   * die des Renders, in dem der Handler lief - wechselt die Instanz waehrend des Uploads die Bestellung, bleibt die
   * Sperre bei der Bestellung, an der hochgeladen wird. Ist die Position schon belegt, beginnt kein Lauf, und es steht
   * eine Meldung (`POSITION_BELEGT_TEXT`): ein Start, der still ins Leere liefe, wirkte wie ein verlorener Klick.
   *
   * Der Aufrufer fuehrt den Lauf SOFORT mit `mitFortschritt` aus (dessen `finally` beendet ihn), und er ruft diese
   * Funktion VOR allem, was nur bei einem Start gelten soll - der Ersatz-Dialog etwa schliesst erst danach, damit
   * bei einer belegten Position Datei und Begruendung erhalten bleiben.
   */
  const laufBeginnen = (jtlPosId: number): UploadLauf | null => {
    const lauf = stellen.versucheBeginn(uploadStelle.position(bestellungId, jtlPosId));
    if (lauf === null) setFehler(POSITION_BELEGT_TEXT);
    return lauf;
  };

  /**
   * Fuehrt den Upload eines begonnenen Laufs (`laufBeginnen`) mit einer Fortschrittszeile aus. `hochladen` bekommt den
   * `fortschritt`-Rueckruf der Upload-Funktion; die Zeile (mit dem Dokumenttyp als Marke) steht, bis der Lauf endet.
   * Er endet erst NACH dem Nachladen der Positionsdokumente, im `finally`, auch nach einem Fehler: bis dahin zeigt
   * eine neue Instanz sonst "frei, aber alter Stand", und ein schneller zweiter Upload liefe mit veralteter
   * `rowVersion` in den 409. Ein Fehler des Uploads wie des Nachladens geht an den Aufrufer (`uploadFehlerMelden`); ein
   * gescheitertes Nachladen kommt als `NachladenNachUploadFehler` (`nachUploadNeuLaden`), damit der Text sagt, dass die
   * Datei gespeichert ist.
   */
  const mitFortschritt = async (
    lauf: UploadLauf,
    typ: PositionsDokumenttyp,
    datei: File,
    hochladen: (fortschritt: (anteil: number) => void) => Promise<unknown>,
  ): Promise<void> => {
    try {
      const zeige = (anteil: number): void => lauf.zeige({ dateiname: datei.name, phase: "hochladen", anteil, marke: typ });
      zeige(0);
      await hochladen(zeige);
      await nachUploadNeuLaden(datei);
    } finally {
      lauf.beende();
    }
  };

  /**
   * Das Nachladen NACH einem gelungenen Upload. Scheitert nur dieses, ist die Datei gespeichert: der Fehler wird als
   * `NachladenNachUploadFehler` gekennzeichnet, damit `uploadFehlerText` "gespeichert, die Anzeige ist aber nicht mehr
   * aktuell" sagt und nicht "konnte nicht gespeichert werden" - der Nutzer luede die Datei sonst ein zweites Mal hoch
   * (Spec R4, Merge-Auftrag R-M11). Dieselbe Kennzeichnung wie im Drawer (`useDrawerDokumente.ts`).
   *
   * Heute erreicht der Zweig im Betrieb nicht: der Rueckruf der App (`App.tsx#positionsDokumenteNeuLaden`) lehnt nie ab,
   * sondern meldet ein gescheitertes Nachladen selbst, mit dem Satz "Die Änderung ist gespeichert, … Bitte neu laden." und
   * ohne Wiederholungsrat (R-M16). Dieser Vertrag schuetzt nur einen kuenftigen Aufrufer, der ablehnt.
   */
  const nachUploadNeuLaden = async (datei: File): Promise<void> => {
    try {
      await positionsDokumenteUndVorschlaegeNeuLaden();
    } catch (ursache) {
      throw new NachladenNachUploadFehler(
        `Die Positionsdokumente konnten nach dem Hochladen von "${datei.name}" nicht neu geladen werden.`,
        { cause: ursache },
      );
    }
  };

  const artworkHochladen = async (
    lauf: UploadLauf,
    jtlPosId: number,
    datei: File,
    notiz?: string,
    rowVersion?: number | null,
  ): Promise<void> => {
    historieInvalidieren();
    setFehler(null);
    const kontext = uploadKontext("artwork", jtlPosId);
    try {
      await mitFortschritt(lauf, "artwork", datei, (fortschritt) => positionsDokumentHochladen(
        bestellungId,
        jtlPosId,
        "artwork",
        datei,
        rowVersion ?? aktuellesDokument(jtlPosId, "artwork")?.rowVersion ?? null,
        notiz,
        fortschritt,
      ));
    } catch (ursache) {
      console.error("Artwork konnte nicht hochgeladen werden:", ursache);
      uploadFehlerMelden(kontext, ursache, datei);
    }
  };

  const artworkGewaehlt = (jtlPosId: number, datei: File): void => {
    if (positionGesperrt(jtlPosId) || !dateiZulaessig(datei)) return;
    const dokument = aktuellesDokument(jtlPosId, "artwork");
    if (dokument) {
      historieInvalidieren();
      setAusstehenderErsatz({ typ: "artwork", datei, jtlPosId, rowVersion: dokument.rowVersion });
      setErsatznotiz("");
      return;
    }
    const lauf = laufBeginnen(jtlPosId);
    if (lauf === null) return;
    void artworkHochladen(lauf, jtlPosId, datei);
  };

  const produktionsbildHinzufuegen = async (jtlPosId: number, datei: File): Promise<void> => {
    if (positionGesperrt(jtlPosId) || !dateiZulaessig(datei)) return;
    const lauf = laufBeginnen(jtlPosId);
    if (lauf === null) return;
    historieInvalidieren();
    setFehler(null);
    const kontext = uploadKontext("produktionsbilder", jtlPosId);
    try {
      await mitFortschritt(lauf, "produktionsbilder", datei, (fortschritt) => produktionsbildHochladen(bestellungId, jtlPosId, datei, undefined, fortschritt));
    } catch (ursache) {
      console.error("Produktionsbild konnte nicht hinzugefügt werden:", ursache);
      uploadFehlerMelden(kontext, ursache, datei);
    }
  };

  const historieLaden = async (typ: Historie["typ"], jtlPosId: number): Promise<void> => {
    const kontext: HistorieLadeKontext = {
      bestellungId,
      jtlPosId,
      typ,
      ladefolge: ++historieLadefolge.current,
    };
    historieKontextRef.current = kontext;
    try {
      if (typ === "produktionsbilder") {
        const bilder = await produktionsbildHistorieLaden(kontext.bestellungId, kontext.jtlPosId);
        if (!istAktuelleHistorie(kontext)) return;
        setHistorie({ typ, bilder });
        return;
      }
      const versionen = await positionsDokumentVersionenLaden(kontext.bestellungId, kontext.jtlPosId, typ);
      if (!istAktuelleHistorie(kontext)) return;
      setHistorie({ typ, versionen });
    } catch (ursache) {
      if (!istAktuelleHistorie(kontext)) return;
      console.error(`Versionshistorie für Positionsdokument "${typ}" konnte nicht geladen werden:`, ursache);
      meldeFehler(ursache, typ === "artwork" ? "Die Artwork-Historie konnte nicht geladen werden." : "Die Produktionsbild-Historie konnte nicht geladen werden.");
    }
  };

  const vorhandenesDokumentUebernehmen = async (typ: PositionsDokumenttyp, jtlPosId: number): Promise<void> => {
    if (!quellDokument || !quelleGiltFuerPosition(jtlPosId)) return;
    historieInvalidieren();
    try {
      await positionsDokumentVerknuepfen(bestellungId, jtlPosId, typ, quellDokument.documentId);
    } catch (ursache) {
      console.error(`Vorhandenes Positionsdokument "${typ}" konnte nicht übernommen werden:`, ursache);
      meldeFehler(ursache, "Das vorhandene Positionsdokument konnte nicht übernommen werden.");
      return;
    }
    // `nachladen` laedt auch die Vorschlaege neu: die uebernommene Datei blendet das Backend dort aus.
    await nachladen("Das vorhandene Positionsdokument ist übernommen");
  };

  /**
   * Laedt die Liste „Entfernte Dokumente“ neu (Ticket F). Scheitert das, ist das Schreiben davor gelungen: der Satz sagt es
   * und nennt die veraltete Liste (`entfernteListeVeraltet`, R4). Ohne `entfernteNeuLaden` passiert nichts.
   */
  const entfernteNachladen = async (zustand: string): Promise<void> => {
    if (!entfernteNeuLaden) return;
    try {
      await entfernteNeuLaden();
    } catch (ursache) {
      console.error("Entfernte Dokumente konnten nicht neu geladen werden:", ursache);
      meldeNachladeFehler(ursache, entfernteListeVeraltet(zustand));
    }
  };

  /** Nach einem gelungenen Entfernen oder Loeschen: Positionsdokumente UND „Entfernte Dokumente“ neu laden (jede Meldung kommt von dort). */
  const nachEntfernenNeuLaden = async (zustand: string): Promise<void> => {
    await Promise.all([nachladen(zustand), entfernteNachladen(zustand)]);
  };

  /** Fuehrt ein Entfernen bzw. Loeschen unter der Doppelklick-Sperre aus; laeuft schon eines, passiert nichts. */
  const mitEntfernenSperre = async (lauf: () => Promise<void>): Promise<void> => {
    if (entfernenLaeuft.current) return;
    entfernenLaeuft.current = true;
    setEntfernenAktiv(true);
    try {
      await lauf();
    } finally {
      entfernenLaeuft.current = false;
      setEntfernenAktiv(false);
    }
  };

  /**
   * Nach einem 409 (die Zeile traegt eine veraltete `rowVersion`, oder das Ziel ist belegt): die Positionsdokumente neu laden,
   * damit der naechste Versuch den aktuellen Stand sieht. Scheitert das Laden, sagt es ein eigener Satz; die Meldung zum Konflikt
   * selbst steht dann schon im Stapel. Fuer `artworkEntfernen` und das Umhaengen.
   */
  const nachKonfliktNeuLaden = async (): Promise<void> => {
    try {
      await positionsDokumenteUndVorschlaegeNeuLaden();
    } catch (nachladeUrsache) {
      console.error("Positionsdokumente konnten nach dem Konflikt nicht neu geladen werden:", nachladeUrsache);
      meldeFehler(nachladeUrsache, "Die Positionsdokumente konnten nicht neu geladen werden.");
    }
  };

  /**
   * Artwork entfernen (Ticket F): Rueckfrage wie beim Entfernen eines Bestelldokuments, dann derselbe Schreibweg
   * (`DELETE .../document-links/:linkId` mit `rowVersion` und `reichweite: "bestellung"`). Danach hat die Position kein Artwork
   * mehr: Hochladen und Uebernehmen stehen wieder zur Verfuegung. Kein Wiederherstellen fuer Artwork.
   */
  const artworkEntfernen = async (dokument: PositionsDokument): Promise<void> => {
    if (entfernenLaeuft.current) return;
    if (!confirm(`„${dokument.originalName}“ wirklich entfernen?`)) return;
    await mitEntfernenSperre(async () => {
      historieInvalidieren();
      try {
        await dokumentEntfernen(bestellungId, dokument.linkId, dokument.rowVersion, "bestellung");
      } catch (ursache) {
        console.error("Artwork konnte nicht entfernt werden:", ursache);
        if (ursache instanceof KonfliktFehler) {
          // Sondertext: er sagt, was zu tun ist, und der Server liefert dafuer keinen eigenen.
          meldeFehler(ARTWORK_ENTFERNEN_KONFLIKT_MELDUNG);
          // Die Zeile ist veraltet: neu laden, damit der naechste Versuch die aktuelle rowVersion traegt.
          await nachKonfliktNeuLaden();
        } else {
          meldeFehler(ursache, ARTWORK_ENTFERNEN_FEHLGESCHLAGEN_MELDUNG);
        }
        return;
      }
      await nachEntfernenNeuLaden("Das Artwork ist entfernt");
    });
  };

  /** Produktionsbild loeschen: seit Ticket F erst nach Rueckfrage (das Wiederherstellen im Historiendialog bleibt ohne). */
  const produktionsbildAusblenden = async (bild: PositionsDokument): Promise<void> => {
    if (entfernenLaeuft.current) return;
    if (!confirm(`„${bild.originalName}“ wirklich löschen?`)) return;
    await mitEntfernenSperre(async () => {
      try {
        await produktionsbildLoeschen(bestellungId, bild.jtlPosId, bild.linkId, bild.rowVersion);
      } catch (ursache) {
        console.error("Das Produktionsbild konnte nicht gelöscht werden.", ursache);
        meldeFehler(ursache, "Das Produktionsbild konnte nicht gelöscht werden.");
        return;
      }
      setAusgeblendeteProduktionsbildLinks((links) => [...links, bild.linkId]);
      await nachEntfernenNeuLaden("Das Produktionsbild ist gelöscht");
    });
  };

  /**
   * Haengt ein Dokument ohne Position an eine aktuelle Position (Positionsersatz C1, `POST .../document-links/:linkId/umhaengen`).
   * Unter derselben Doppelklick-Sperre wie das Entfernen: waehrend die Anfrage laeuft, traegt die Zeile noch die alte `rowVersion`.
   * Alle Meldungen gehen in den Fehlerstapel (ausserhalb dieser Instanz): ein Fehler, der nach einem Reiterwechsel eintrifft,
   * bleibt so sichtbar. Nach dem await geht nichts in den State dieser Instanz ausser dem Ende der Sperre, das mit ihr verfallen
   * darf - es gibt keine eigene Zeile, die bei einer abgebauten Instanz verloren ginge (anders als beim Upload, `uploadFehlerMelden`).
   *
   * 409 mit aktuellem Stand: die Zeile hat sich geaendert - Sondertext, neu laden (frische `rowVersion`). 409 ohne: das Ziel hat
   * schon ein Artwork (Wettlauf) - der Servertext sagt welches; neu laden, damit die Auswahl es sperrt. Alles andere (422, 400,
   * Netz): Meldung mit Servertext, ohne Neuladen - die Anzeige ist nicht veraltet, und der Nutzer kann ein anderes Ziel waehlen.
   */
  const dokumentUmhaengen = async (dokument: PositionsDokument, ziel: { jtlPosId: number; bezeichnung: string }): Promise<void> => {
    if (entfernenLaeuft.current) return;
    await mitEntfernenSperre(async () => {
      try {
        await positionsDokumentUmhaengen(bestellungId, dokument.linkId, ziel.jtlPosId, dokument.rowVersion);
      } catch (ursache) {
        console.error("Positionsdokument konnte nicht umgehängt werden:", ursache);
        if (ursache instanceof KonfliktFehler && ursache.aktuellerStand !== undefined) {
          meldeFehler(UMHAENGEN_KONFLIKT_MELDUNG);
        } else {
          meldeFehler(ursache, UMHAENGEN_FEHLGESCHLAGEN_MELDUNG);
        }
        if (ursache instanceof KonfliktFehler) await nachKonfliktNeuLaden();
        return;
      }
      await nachladen(`Das Dokument hängt jetzt an ${ziel.bezeichnung}`);
    });
  };

  const produktionsbildRestore = async (bild: ProduktionsbildHistorienElement): Promise<void> => {
    const kontext = historieKontextRef.current;
    try {
      await produktionsbildWiederherstellenApi(bestellungId, bild.jtlPosId, bild.linkId, bild.rowVersion);
    } catch (ursache) {
      console.error("Das Produktionsbild konnte nicht wiederhergestellt werden.", ursache);
      // Der Historie-Dialog bleibt offen: der Eintrag erscheint im Stapel darueber (Review Focus 2).
      meldeFehler(ursache, "Das Produktionsbild konnte nicht wiederhergestellt werden.");
      return;
    }
    // Das Bild steht wieder in der Galerie und gehoert nicht mehr in „Entfernte Dokumente“: beide Listen neu laden.
    const [positionenAktuell] = await Promise.all([
      nachladen("Das Produktionsbild ist wiederhergestellt"),
      entfernteNachladen("Das Produktionsbild ist wiederhergestellt"),
    ]);
    if (!positionenAktuell) return;
    if (kontext && istAktuelleHistorie(kontext)) void historieLaden("produktionsbilder", bild.jtlPosId);
  };

  const historieSchliessen = (): void => {
    historieInvalidieren();
    queueMicrotask(() => historieAusloeserRef.current?.focus());
  };

  const pruefungOeffnen = (linkId: string, ausloeser: HTMLElement): void => {
    pruefungAusloeserRef.current = ausloeser;
    pruefungZeileRef.current = zeileDesAbzeichens(ausloeser);
    setPruefungOffen(linkId);
  };

  const pruefungSchliessen = (): void => {
    setPruefungOffen(null);
    fokusZumAbzeichen(pruefungAusloeserRef.current, pruefungZeileRef.current);
  };

  // „Prüfen“ / „Erneut prüfen“ und „Gesehen“: derselbe Weg und dieselben Meldungen wie in der Zusammenfassung des Massenuploads.
  const { anstossen: pruefungAnstossen, alsGesehen: pruefungAlsGesehen } = pruefAktionen(
    bestellungId,
    pruefungenNeuLaden,
    PRUEF_MELDER,
    bestellungNachladen,
  );

  const ersatzSchliessen = (): void => {
    setAusstehenderErsatz(null);
    queueMicrotask(() => ersatzAusloeserRef.current?.focus());
  };

  const ersatzBestaetigen = (): void => {
    const ersatz = ausstehenderErsatz;
    if (!ersatz) return;
    // Der Lauf beginnt VOR dem Schliessen des Dialogs. Ist die Position inzwischen belegt (ein anderer Upload hat sie
    // genommen), startet nichts - das darf nicht still geschehen: eine Meldung, der Dialog bleibt offen und behaelt
    // Datei und Begruendung. Normalerweise kommt ein Klick gar nicht an, denn "Ersatz hochladen" ist bei belegter
    // Position `disabled`.
    const lauf = laufBeginnen(ersatz.jtlPosId);
    if (lauf === null) return;
    ersatzSchliessen();
    if (ersatz.typ === "artwork") {
      void artworkHochladen(lauf, ersatz.jtlPosId, ersatz.datei, ersatznotiz.trim(), ersatz.rowVersion);
      return;
    }
    historieInvalidieren();
    setFehler(null);
    const kontext = uploadKontext("produktionsbilder", ersatz.jtlPosId);
    void mitFortschritt(lauf, "produktionsbilder", ersatz.datei, (fortschritt) => produktionsbildErsetzen(
      bestellungId, ersatz.jtlPosId, ersatz.linkId, ersatz.datei, ersatz.rowVersion, ersatznotiz.trim(), fortschritt,
    ))
      .catch((ursache) => {
        console.error("Das Produktionsbild konnte nicht ersetzt werden.", ursache);
        uploadFehlerMelden(kontext, ursache, ersatz.datei);
      });
  };

  return (
    <div className="positions-dokumente">
      {/* Ladefehler (L1, Fehleranzeige Paket 2, Task 13): statt des Abschnitts samt Knoepfen EINE Flaeche mit Grund und „Erneut laden“ - kein
          „Noch keine Fassung.“ auf unbekanntem Stand. `data-permission="view_board"`: lesender Neuversuch, der Rechte-Durchgang des Drawers
          (`Drawer.tsx`) sperrt sonst jeden Knopf ohne `manage_documents` (Vorbild `DokumenteTab`). `key={bestellungId}`: dieselbe Flaeche steht
          nacheinander fuer wechselnde Bestellungen; der Schluessel setzt die Sperre ihres Knopfes beim Wechsel zurueck. Fehlt der Rueckruf
          oder ergibt die Ursache keinen Text (Abbruch, 401), zeichnet sich nichts. */}
      {positionsDokumenteZustand.art === "gescheitert" ? (
        <div data-permission="view_board">
          <Ladefehler
            key={bestellungId}
            was={POSITIONSDOKUMENTE_LADEFEHLER_SATZ}
            ursache={positionsDokumenteZustand.ursache}
            onErneutLaden={onPositionsDokumenteErneutLaden}
          />
        </div>
      ) : null}
      {positionsDokumenteZustand.art === "laedt" ? <p className="positionsdokumente-laedt">Lädt…</p> : null}

      {geladen && hatPersistiertePositionen ? DOKUMENTTYPEN.map(({ typ, label }) => (
        <section className="positionsdokument-typ" aria-labelledby={`positionsdokument-typ-${typ}`} key={typ}>
          <h4 id={`positionsdokument-typ-${typ}`}>{label}</h4>
          <div className="doc-list">
            {positionen.map((position, index) => {
              const jtlPosId = positionskennung(position);
              if (jtlPosId === null) return null;
              const dokument = typ === "artwork" ? aktuellesDokument(jtlPosId, "artwork") : undefined;
              const schluessel = inputSchluessel(typ, jtlPosId);
              const belegt = positionBelegt(jtlPosId);
              return (
                <section
                  ref={(element) => {
                    const schluessel = `${typ}:${jtlPosId}`;
                    if (element) positionsRefs.current.set(schluessel, element);
                    else positionsRefs.current.delete(schluessel);
                  }}
                  className="positionsdokument-position"
                  data-massenupload-position={jtlPosId}
                  aria-label={`${label} – ${positionsbezeichnung(position, index)}`}
                  key={jtlPosId}
                  tabIndex={-1}
                >
                  <h5>{positionsbezeichnung(position, index)}</h5>
                  {typ === "artwork" ? <div className="doc-row">
                    <div className="doc-ic"><Ikone name="image" /></div>
                    <div><strong>Artwork</strong>{dokument ? <span>{`Version ${dokument.version} · ${dokument.originalName}`}</span> : <span>Noch keine Fassung.</span>}</div>
                    {dokument ? <ArtworkPruefungAbzeichen pruefung={pruefungVon(dokument)} onOeffnen={(ausloeser) => pruefungOeffnen(dokument.linkId, ausloeser)} /> : null}
                    {dokument ? <button type="button" className="chk-btn" aria-label="Artwork öffnen" onClick={() => void dateiOeffnen(dokument.fileId, dokument.originalName)}>Öffnen</button> : null}
                    {dokument && darfArtworkEntfernen ? <button type="button" className="row-del" aria-label="Artwork entfernen" title="Artwork entfernen" disabled={belegt || entfernenAktiv} onClick={() => void artworkEntfernen(dokument)}><Ikone name="trash-2" /></button> : null}
                    <button type="button" className="chk-btn" aria-label="Artwork Historie" onClick={(event) => { historieAusloeserRef.current = event.currentTarget; void historieLaden("artwork", jtlPosId); }}><Ikone name="history" />Historie</button>
                    <button type="button" className="chk-btn" disabled={belegt} onClick={(event) => { ersatzAusloeserRef.current = event.currentTarget; dateiInputs.current.get(schluessel)?.click(); }}><Ikone name="upload" />Artwork hochladen</button>
                    <input ref={(element) => { if (element) dateiInputs.current.set(schluessel, element); else dateiInputs.current.delete(schluessel); }} type="file" aria-label="Artwork hochladen" disabled={belegt} style={{ display: "none" }} onChange={(event) => { const datei = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (datei) artworkGewaehlt(jtlPosId, datei); }} />
                    <button type="button" className="chk-btn" disabled={!quellDokument || Boolean(dokument) || !quelleGiltFuerPosition(jtlPosId)} title={nurAnPositionenMitArtikel(jtlPosId)} aria-label="Artwork aus vorhandenem Dokument übernehmen" onClick={() => void vorhandenesDokumentUebernehmen("artwork", jtlPosId)}><Ikone name="image" />Übernehmen</button>
                  </div> : <section className="produktionsbild-galerie" aria-label={`Produktionsbilder für ${positionsbezeichnung(position, index)}`}>
                    <div className="produktionsbild-galerie-kopf"><div><strong>Produktionsbilder</strong><span>Mehrere Bilder pro Position</span></div><div>
                      <button type="button" className="chk-btn" aria-label="Produktionsbilder Historie" onClick={(event) => { historieAusloeserRef.current = event.currentTarget; void historieLaden("produktionsbilder", jtlPosId); }}><Ikone name="history" />Historie</button>
                      <button type="button" className="chk-btn" disabled={belegt} onClick={(event) => { ersatzAusloeserRef.current = event.currentTarget; dateiInputs.current.get(schluessel)?.click(); }}><Ikone name="upload" />Bild hinzufügen</button>
                    </div></div>
                    <input ref={(element) => { if (element) dateiInputs.current.set(schluessel, element); else dateiInputs.current.delete(schluessel); }} type="file" aria-label={`Produktionsbild für ${positionsbezeichnung(position, index)} hinzufügen`} disabled={belegt} style={{ display: "none" }} onChange={(event) => { const datei = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (datei) void produktionsbildHinzufuegen(jtlPosId, datei); }} />
                    {produktionsbilder(jtlPosId).length ? <div className="produktionsbild-galerie-raster">{produktionsbilder(jtlPosId).map((bild, bildIndex) => {
                      const ersatzSchluessel = inputSchluessel("produktionsbild-ersatz", jtlPosId, bild.linkId);
                      const bildnummer = bildIndex + 1;
                      const bildbezeichnung = `${bildnummer}: ${bild.originalName}`;
                      return <article className="produktionsbild-karte" key={bild.linkId} tabIndex={0} aria-label={`Produktionsbild ${bildbezeichnung}`}><ProduktionsbildThumbnail bestellungId={bestellungId} bild={bild} onProduktionsbildOeffnen={onProduktionsbildOeffnen} /><strong>{bild.originalName}</strong><span>{`Bild ${bildnummer} · Version ${bild.version}`}</span><div className="produktionsbild-aktionen">
                        <button type="button" className="chk-btn" aria-label={`Bild ${bildbezeichnung} öffnen`} onClick={(event) => onProduktionsbildOeffnen?.(bild.linkId, event.currentTarget)}>Öffnen</button>
                        <button type="button" className="chk-btn" aria-label={`Bild ${bildbezeichnung} ersetzen`} disabled={belegt} onClick={(event) => { ersatzAusloeserRef.current = event.currentTarget; dateiInputs.current.get(ersatzSchluessel)?.click(); }}>Ersetzen</button>
                        <button type="button" className="chk-btn produktionsbild-loeschen" aria-label={`Bild ${bildbezeichnung} löschen`} disabled={entfernenAktiv} onClick={() => void produktionsbildAusblenden(bild)}>Löschen</button>
                      </div><input ref={(element) => { if (element) dateiInputs.current.set(ersatzSchluessel, element); else dateiInputs.current.delete(ersatzSchluessel); }} type="file" aria-label={`Bild ${bildbezeichnung} Ersatzdatei wählen`} disabled={belegt} style={{ display: "none" }} onChange={(event) => { const datei = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (datei && !positionGesperrt(jtlPosId) && dateiZulaessig(datei)) { historieInvalidieren(); setAusstehenderErsatz({ typ: "produktionsbilder", datei, jtlPosId, linkId: bild.linkId, rowVersion: bild.rowVersion }); setErsatznotiz(""); } }} /></article>;
                    })}</div> : <p className="produktionsbild-leer">Noch keine Produktionsbilder.</p>}
                    <button type="button" className="chk-btn" disabled={!quellDokument || !quelleGiltFuerPosition(jtlPosId)} title={nurAnPositionenMitArtikel(jtlPosId)} aria-label="Produktionsbilder aus vorhandenem Dokument übernehmen" onClick={() => void vorhandenesDokumentUebernehmen("produktionsbilder", jtlPosId)}><Ikone name="image" />Übernehmen</button>
                  </section>}
                </section>
              );
            })}
          </div>
        </section>
      )) : null}

      {geladen && waisen.length ? (
        <section className="positionsdokument-waisen" aria-label="Dokumente ohne Position">
          <h4>Dokumente ohne Position</h4>
          <p className="positionsdokument-waisen-hinweis">
            Diese Dokumente hängen an einer Position, die JTL in dieser Bestellung nicht mehr führt.
            {darfArtworkEntfernen ? " Hängen Sie sie an eine aktuelle Position oder entfernen Sie sie." : ""}
          </p>
          <div className="doc-list">
            {waisen.map((waise) => {
              const label = DOKUMENTTYPEN.find((eintrag) => eintrag.typ === waise.docType)?.label ?? "Positionsdokument";
              const ziel = gewaehltesZiel(waise);
              // Jeder Name nennt die alte Position: hing dasselbe Dokument an zwei Positionen, die JTL beide ersetzt hat, bliebe sonst
              // jeder Name doppelt (Gruppe, Auswahl, Knoepfe).
              const stelle = `(JTL-Position ${waise.jtlPosId})`;
              const istBild = waise.docType === "produktionsbilder";
              return (
                <div className="doc-row positionsdokument-waise" role="group" aria-label={`${label}: ${waise.originalName} ${stelle}`} key={waise.linkId}>
                  {/* Wie in der Positionszeile: das Produktionsbild mit Vorschau (und Lightbox), das Artwork mit dem Symbol. */}
                  {istBild
                    ? <div className="positionsdokument-waise-vorschau"><ProduktionsbildThumbnail bestellungId={bestellungId} bild={waise} onProduktionsbildOeffnen={onProduktionsbildOeffnen} /></div>
                    : <div className="doc-ic"><Ikone name="image" /></div>}
                  <div className="positionsdokument-waise-text"><strong>{label}</strong><span>{`${waise.originalName} · JTL-Position ${waise.jtlPosId}`}</span></div>
                  <button
                    type="button"
                    className="chk-btn"
                    aria-label={`${waise.originalName} öffnen ${stelle}`}
                    onClick={(event) => (istBild ? onProduktionsbildOeffnen?.(waise.linkId, event.currentTarget) : void dateiOeffnen(waise.fileId, waise.originalName))}
                  >Öffnen</button>
                  {/* Schreiben nur mit dem Recht, das auch Hochladen und Entfernen verlangen (`manage_documents`, wie der Muelleimer am Artwork). */}
                  {darfArtworkEntfernen ? <>
                    <select
                      aria-label={`Zielposition für ${waise.originalName} ${stelle}`}
                      value={ziel ? String(ziel.jtlPosId) : ""}
                      disabled={entfernenAktiv}
                      onChange={(event) => { const wert = event.currentTarget.value; setUmhaengenZiele((ziele) => ({ ...ziele, [waise.linkId]: wert })); }}
                    >
                      <option value="">— Position wählen —</option>
                      {zielPositionen.map((position) => {
                        const gesperrt = umhaengenZielGesperrt(waise, position.jtlPosId);
                        return <option key={position.jtlPosId} value={position.jtlPosId} disabled={gesperrt}>{gesperrt ? `${position.bezeichnung} (hat schon ein Artwork)` : position.bezeichnung}</option>;
                      })}
                    </select>
                    <button type="button" className="chk-btn" aria-label={`${waise.originalName} an Position hängen ${stelle}`} disabled={!ziel || entfernenAktiv} onClick={() => { if (ziel) void dokumentUmhaengen(waise, ziel); }}>An Position hängen</button>
                    <button
                      type="button"
                      className="row-del"
                      aria-label={`${waise.originalName} entfernen ${stelle}`}
                      title={`${waise.originalName} entfernen ${stelle}`}
                      disabled={entfernenAktiv}
                      onClick={() => void (istBild ? produktionsbildAusblenden(waise) : artworkEntfernen(waise))}
                    ><Ikone name="trash-2" /></button>
                  </> : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {geladen && hatPersistiertePositionen && (positionsDokumente.length > 0 || vorschlaege.length > 0) ? (
        <div className="field">
          <label htmlFor="positionsdokument-quelle">Vorhandenes Positionsdokument übernehmen</label>
          <select
            id="positionsdokument-quelle"
            value={quellDokumentId}
            onChange={(event) => setQuellDokumentId(event.currentTarget.value)}
          >
            <option value="">— Datei auswählen —</option>
            {positionsDokumente.map((dokument) => (
              <option key={dokument.documentId} value={dokument.documentId}>
                {`${dokument.originalName} · Position ${dokument.jtlPosId}`}
              </option>
            ))}
            {vorschlaege.length > 0 ? (
              <optgroup label="Aus anderen Bestellungen">
                {vorschlaege.map((vorschlag) => (
                  <option key={vorschlag.linkId} value={vorschlag.documentId}>
                    {`${DOKUMENTTYPEN.find((eintrag) => eintrag.typ === vorschlag.docType)?.label ?? "Positionsdokument"} · ${vorschlag.originalName} · ${vorschlag.bestellnummer}`}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </div>
      ) : null}

      {!hatPersistiertePositionen ? <p>Keine persistierten JTL-Bestellpositionen vorhanden.</p> : null}

      {offenesArtwork ? (
        <ArtworkPruefungEinzelheitenLazy
          pruefung={pruefungVon(offenesArtwork) ?? nichtGeprueft(offenesArtwork)}
          darfAendern={darfPruefungAendern}
          onSchliessen={pruefungSchliessen}
          onErneutPruefen={() => pruefungAnstossen(offenesArtwork.linkId)}
          onGesehen={() => pruefungAlsGesehen(offenesArtwork.linkId)}
          melder={PRUEF_MELDER}
        />
      ) : null}

      {historie?.typ === "artwork" ? createPortal(
        <div className="board-editor-overlay open positionsdokument-modal-overlay" role="presentation">
          <section ref={historieDialogRef} className="board-editor-dialog overwrite-dialog" role="dialog" aria-modal="true" aria-label="Artwork Versionshistorie">
            <div className="board-editor-head"><h2>Artwork – Versionen</h2></div>
            <div className="board-editor-body">
              {historie.versionen.length ? (
                <ul>
                  {historie.versionen.map((version) => (
                    <li key={`${version.version}-${version.fileId}`}>
                      <span>{`Version ${version.version}`}</span>
                      <button type="button" onClick={() => void dateiOeffnen(version.fileId, version.originalName)}>
                        Öffnen
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Keine älteren Fassungen vorhanden.</p>
              )}
            </div>
            <div className="board-editor-foot"><button ref={historieSchliessenRef} type="button" className="chk-btn" onClick={historieSchliessen}>Schließen</button></div>
          </section>
        </div>,
        document.body,
      ) : null}

      {historie?.typ === "produktionsbilder" ? createPortal(
        <div className="board-editor-overlay open positionsdokument-modal-overlay" role="presentation">
          <section ref={historieDialogRef} className="board-editor-dialog overwrite-dialog" role="dialog" aria-modal="true" aria-label="Produktionsbilder Historie">
            <div className="board-editor-head"><h2>Produktionsbilder – Historie</h2></div>
            <div className="board-editor-body">
              {historie.bilder.length ? <ul className="produktionsbild-historie">{historie.bilder.map((bild) => (
                <li className={bild.deletedAt === null ? "produktionsbild-aktiv" : "produktionsbild-geloescht"} key={bild.linkId}>
                  <strong>{bild.originalName}</strong>
                  {bild.deletedAt !== null ? <span>{`Gelöscht am ${new Date(bild.deletedAt).toLocaleString()}${bild.deletedBy ? ` von ${bild.deletedBy}` : ""}`}</span> : <span>Aktiv</span>}
                  <ul className="produktionsbild-versionen">{bild.versionen.map((version) => <li key={`${version.version}-${version.fileId}`}><strong>{`Version ${version.version} · ${version.originalName}`}</strong>{version.note ? <span>{version.note}</span> : null}<time dateTime={version.ts}>{new Date(version.ts).toLocaleString()}</time><button type="button" onClick={() => void dateiOeffnen(version.fileId, version.originalName)}>Öffnen</button></li>)}</ul>
                  {bild.ereignisse.length ? <ul className="produktionsbild-ereignisse">{bild.ereignisse.map((ereignis, index) => <li key={`${ereignis.ts}-${index}`}><span>{`${GALERIE_EREIGNIS_TEXT.get(ereignis.neuWert ?? "") ?? ereignis.neuWert ?? "Ereignis"}${ereignis.aktor ? ` · ${ereignis.aktor}` : ""}`}</span><time dateTime={ereignis.ts}>{new Date(ereignis.ts).toLocaleString()}</time></li>)}</ul> : null}
                  {bild.deletedAt !== null ? <button type="button" onClick={() => void produktionsbildRestore(bild)} aria-label={`${bild.originalName} wiederherstellen`}>Wiederherstellen</button> : null}
                </li>
              ))}</ul> : <p>Keine Produktionsbilder vorhanden.</p>}
            </div>
            <div className="board-editor-foot"><button ref={historieSchliessenRef} type="button" className="chk-btn" onClick={historieSchliessen}>Schließen</button></div>
          </section>
        </div>,
        document.body,
      ) : null}

      {ausstehenderErsatz ? (
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${ausstehenderErsatz.typ === "artwork" ? "Artwork" : "Produktionsbild"} ersetzen`}>
          <h4>{`${ausstehenderErsatz.typ === "artwork" ? "Artwork" : "Produktionsbild"} ersetzen`}</h4>
          <label htmlFor="positionsdokument-ersatznotiz">
            {`Begründung für ${ausstehenderErsatz.typ === "artwork" ? "Artwork" : "Produktionsbild"}`}
          </label>
          <textarea
            ref={ersatznotizRef}
            id="positionsdokument-ersatznotiz"
            value={ersatznotiz}
            onChange={(event) => setErsatznotiz(event.currentTarget.value)}
          />
          <button type="button" onClick={ersatzSchliessen}>Abbrechen</button>
          <button
            type="button"
            disabled={!ersatznotiz.trim() || positionBelegt(ausstehenderErsatz.jtlPosId)}
            onClick={ersatzBestaetigen}
          >
            Ersatz hochladen
          </button>
        </div>
      ) : null}

      {/* Dauerhaft gemountet, die Zeilen darunter nur solange ein Lauf besteht (`UploadFortschritt.tsx`, Kopfkommentar). */}
      <UploadAnsage
        anzeigen={laeufe.flatMap(({ stelle, stand }) => (stand.anzeige ? [{ schluessel: stelle, anzeige: stand.anzeige }] : []))}
      />
      {laeufe.map(({ stelle, stand }) => (
        stand.anzeige !== null ? <UploadFortschritt key={stelle} {...stand.anzeige} /> : null
      ))}
      <UploadFehlerAnzeige meldungen={fehler ? [fehler] : []} onSchliessen={() => setFehler(null)} />
    </div>
  );
}
